import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Shield,
  Users,
  Vote,
  Wallet,
  CheckCircle,
  Clock,
  ArrowRight,
  Leaf,
  Scale,
  Eye,
  Lock,
  Handshake
} from 'lucide-react';

export default function GovernancePage() {
  const { t } = useLanguage();

  // Governance principles
  const PRINCIPLES = [
    {
      icon: Eye,
      title: t('governance.principles.radicalTransparency.title'),
      description: t('governance.principles.radicalTransparency.description'),
      color: 'bg-blue-500/10 text-blue-600'
    },
    {
      icon: Users,
      title: t('governance.principles.communityParticipation.title'),
      description: t('governance.principles.communityParticipation.description'),
      color: 'bg-green-500/10 text-green-600'
    },
    {
      icon: Lock,
      title: t('governance.principles.protectedFunds.title'),
      description: t('governance.principles.protectedFunds.description'),
      color: 'bg-purple-500/10 text-purple-600'
    },
    {
      icon: Scale,
      title: t('governance.principles.accountability.title'),
      description: t('governance.principles.accountability.description'),
      color: 'bg-orange-500/10 text-orange-600'
    }
  ];

  // Treasury allocation
  const TREASURY_ALLOCATION = [
    { ...t('governance.treasury.directPlanting'), color: 'bg-green-500' },
    { ...t('governance.treasury.maintenance'), color: 'bg-blue-500' },
    { ...t('governance.treasury.operations'), color: 'bg-purple-500' },
    { ...t('governance.treasury.reserve'), color: 'bg-orange-500' }
  ];

  // Governance roadmap
  const GOVERNANCE_ROADMAP = [
    t('governance.governanceRoadmap.currentPhase'),
    t('governance.governanceRoadmap.v1Phase'),
    t('governance.governanceRoadmap.v2Phase')
  ];

  // Multi-sig signers
  const SIGNERS = [
    t('governance.multiSig.foundation'),
    t('governance.multiSig.council'),
    t('governance.multiSig.auditor')
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="governance-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 pt-16">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Shield className="w-3.5 h-3.5" />
              {t('governance.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              {t('governance.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('governance.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-foreground mb-2">{t('governance.principles.title')}</h2>
            <p className="text-muted-foreground">{t('governance.principles.subtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRINCIPLES.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="natural-card p-6 h-full text-center">
                    <div className={`w-14 h-14 mx-auto mb-4 rounded-lg ${principle.color} flex items-center justify-center`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{principle.title}</h3>
                    <p className="text-sm text-muted-foreground">{principle.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Treasury Allocation */}
      <section className="py-16 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Wallet className="w-3.5 h-3.5" />
              {t('governance.treasury.badge')}
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-2">{t('governance.treasury.title')}</h2>
            <p className="text-muted-foreground">{t('governance.treasury.subtitle')}</p>
          </motion.div>

          <Card className="natural-card p-8">
            {/* Visual bar */}
            <div className="h-8 rounded-full overflow-hidden flex mb-8">
              {TREASURY_ALLOCATION.map((item, index) => (
                <div 
                  key={index}
                  className={`${item.color} h-full transition-all`}
                  style={{ width: `${item.percentage}%` }}
                  title={`${item.category}: ${item.percentage}%`}
                />
              ))}
            </div>

            {/* Legend */}
            <div className="grid sm:grid-cols-2 gap-4">
              {TREASURY_ALLOCATION.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className={`w-4 h-4 rounded ${item.color} flex-shrink-0 mt-1`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{item.category}</span>
                      <span className="text-sm text-primary font-bold">{item.percentage}%</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>

          {/* Transparency link */}
          <motion.div 
            className="mt-6 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Button asChild variant="outline">
              <Link to="/transparency">
                <Eye className="w-4 h-4 mr-2" />
                {t('governance.treasury.viewPublicRecord')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Multi-sig Section */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Lock className="w-3.5 h-3.5" />
              {t('governance.multiSig.badge')}
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-2">{t('governance.multiSig.title')}</h2>
            <p className="text-muted-foreground">{t('governance.multiSig.subtitle')}</p>
          </motion.div>

          <Card className="natural-card p-8">
            <div className="flex items-center justify-center gap-4 mb-8 flex-wrap">
              {SIGNERS.map((signer, index) => (
                <React.Fragment key={index}>
                  <div className="text-center">
                    <div className={`w-16 h-16 mx-auto mb-2 rounded-full ${
                      signer?.required ? 'bg-primary/10 border-2 border-primary' : 'bg-muted'
                    } flex items-center justify-center`}>
                      <Users className={`w-8 h-8 ${signer?.required ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <p className="font-medium text-sm text-foreground">{signer?.name}</p>
                    <p className="text-xs text-muted-foreground">{signer?.role}</p>
                    {signer?.required && (
                      <span className="text-xs text-primary">{t('governance.multiSig.requiredLabel')}</span>
                    )}
                  </div>
                  {index < SIGNERS.length - 1 && (
                    <div className="text-muted-foreground text-2xl">+</div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t('governance.multiSig.requirement')}
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* Governance Roadmap */}
      <section className="py-16 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Vote className="w-3.5 h-3.5" />
              {t('governance.governanceRoadmap.badge')}
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-2">{t('governance.governanceRoadmap.title')}</h2>
            <p className="text-muted-foreground">{t('governance.governanceRoadmap.subtitle')}</p>
          </motion.div>

          <div className="space-y-6">
            {GOVERNANCE_ROADMAP.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`natural-card p-6 ${
                  phase?.status === 'active' ? 'border-2 border-primary' : ''
                }`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center ${
                      phase?.status === 'active' ? 'bg-primary text-primary-foreground' :
                      phase?.status === 'upcoming' ? 'bg-primary/20 text-primary' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {phase?.status === 'active' ? <CheckCircle className="w-6 h-6" /> :
                       <Clock className="w-6 h-6" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs uppercase tracking-wider text-primary font-semibold">{phase?.phase}</span>
                        {phase?.status === 'active' && (
                          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">Activo</span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-3">{phase?.title}</h3>
                      <ul className="grid sm:grid-cols-2 gap-2">
                        {phase?.items?.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className={`w-4 h-4 flex-shrink-0 ${
                              phase?.status === 'active' ? 'text-primary' : 'text-muted-foreground/50'
                            }`} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved CTA */}
      <section className="py-16 px-4 eco-gradient text-white">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
              <Handshake className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold mb-4">{t('governance.cta.title')}</h2>
            <p className="text-white/80 mb-8">
              {t('governance.cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90">
                <Link to="/adopt">
                  {t('governance.cta.adoptTree')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10">
                <Link to="/transparency">
                  {t('governance.cta.viewTransparency')}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-background border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-primary" />
              </div>
              <span className="font-semibold text-lg text-foreground">Epuyén Renace</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Epuyén Renace. {t('landing.footerTagline')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
